import { useState, useEffect, useMemo } from 'react';

interface ScheduleBuilderProps {
  value: string;
  onChange: (expr: string) => void;
}

type Frequency = 'minutes' | 'hourly' | 'daily' | 'weekly' | 'monthly';

const DAYS_OF_WEEK = [
  { value: 'MON', label: 'Mon' },
  { value: 'TUE', label: 'Tue' },
  { value: 'WED', label: 'Wed' },
  { value: 'THU', label: 'Thu' },
  { value: 'FRI', label: 'Fri' },
  { value: 'SAT', label: 'Sat' },
  { value: 'SUN', label: 'Sun' },
];

interface PresetItem {
  label: string;
  expression: string;
}

const PRESETS: PresetItem[] = [
  { label: 'Every 5 min', expression: 'rate(5 minutes)' },
  { label: 'Every 15 min', expression: 'rate(15 minutes)' },
  { label: 'Every hour', expression: 'rate(1 hour)' },
  { label: 'Daily at 9:00', expression: 'cron(0 9 * * ? *)' },
  { label: 'Mon at 9:00', expression: 'cron(0 9 ? * MON *)' },
  { label: '1st of month', expression: 'cron(0 9 1 * ? *)' },
];

function parseTime(hhmm: string): { hour: number; minute: number } {
  const [h, m] = hhmm.split(':').map(Number);
  return { hour: h || 0, minute: m || 0 };
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

/** Compute the next N execution times for display purposes. */
function computeNextExecutions(expr: string, count: number): string[] {
  const results: string[] = [];
  const now = new Date();

  if (expr.startsWith('rate(')) {
    const inner = expr.slice(5, -1).trim();
    const parts = inner.split(/\s+/);
    const amount = parseInt(parts[0], 10);
    const unit = parts[1] || '';
    if (isNaN(amount)) return [];

    let ms = 0;
    if (unit.startsWith('minute')) ms = amount * 60_000;
    else if (unit.startsWith('hour')) ms = amount * 3_600_000;
    else if (unit.startsWith('day')) ms = amount * 86_400_000;
    else return [];

    for (let i = 1; i <= count; i++) {
      const d = new Date(now.getTime() + ms * i);
      results.push(d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC');
    }
  } else if (expr.startsWith('cron(')) {
    // Simple pattern description instead of full cron parser
    const inner = expr.slice(5, -1).trim();
    const parts = inner.split(/\s+/);
    if (parts.length !== 6) return [];

    const [minute, hour, dayOfMonth, , dayOfWeek] = parts;
    const h = parseInt(hour, 10);
    const m = parseInt(minute, 10);

    if (isNaN(h) || isNaN(m)) return ['(cannot preview custom cron)'];

    // Simple forward computation for daily/weekly/monthly patterns
    const candidate = new Date(now);
    candidate.setUTCSeconds(0, 0);
    candidate.setUTCHours(h, m);

    // If specific day of week
    if (dayOfWeek !== '?' && dayOfWeek !== '*') {
      const dayMap: Record<string, number> = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 };
      const targetDays = dayOfWeek.split(',').map((d) => dayMap[d] ?? -1).filter((d) => d >= 0);
      if (targetDays.length === 0) return [];

      // Find next occurrences
      let d = new Date(candidate);
      if (d <= now) d.setUTCDate(d.getUTCDate() + 1);
      for (let attempts = 0; results.length < count && attempts < 60; attempts++) {
        if (targetDays.includes(d.getUTCDay())) {
          const check = new Date(d);
          check.setUTCHours(h, m, 0, 0);
          if (check > now) {
            results.push(check.toISOString().replace('T', ' ').slice(0, 16) + ' UTC');
          }
        }
        d.setUTCDate(d.getUTCDate() + 1);
      }
    } else if (dayOfMonth !== '*' && dayOfMonth !== '?') {
      // Specific day of month
      const dom = parseInt(dayOfMonth, 10);
      if (isNaN(dom)) return [];
      let d = new Date(candidate);
      d.setUTCDate(dom);
      d.setUTCHours(h, m, 0, 0);
      if (d <= now) d.setUTCMonth(d.getUTCMonth() + 1);
      for (let i = 0; i < count; i++) {
        results.push(d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC');
        d.setUTCMonth(d.getUTCMonth() + 1);
      }
    } else {
      // Daily
      let d = new Date(candidate);
      d.setUTCHours(h, m, 0, 0);
      if (d <= now) d.setUTCDate(d.getUTCDate() + 1);
      for (let i = 0; i < count; i++) {
        results.push(d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC');
        d.setUTCDate(d.getUTCDate() + 1);
      }
    }
  }

  return results;
}

export default function ScheduleBuilder({ value, onChange }: ScheduleBuilderProps) {
  const [advanced, setAdvanced] = useState(false);
  const [rawExpr, setRawExpr] = useState(value);

  // Builder state
  const [frequency, setFrequency] = useState<Frequency>('daily');
  const [minuteInterval, setMinuteInterval] = useState(5);
  const [time, setTime] = useState('09:00');
  const [selectedDays, setSelectedDays] = useState<string[]>(['MON']);
  const [dayOfMonth, setDayOfMonth] = useState(1);

  // Sync raw expression when value prop changes externally
  useEffect(() => {
    setRawExpr(value);
  }, [value]);

  // Detect current state from the value prop on mount
  useEffect(() => {
    if (!value) return;
    if (value.startsWith('rate(')) {
      const inner = value.slice(5, -1).trim();
      const parts = inner.split(/\s+/);
      const amount = parseInt(parts[0], 10);
      const unit = parts[1] || '';
      if (unit.startsWith('minute')) {
        setFrequency('minutes');
        setMinuteInterval(amount);
      } else if (unit.startsWith('hour')) {
        setFrequency('hourly');
        setMinuteInterval(amount);
      }
    } else if (value.startsWith('cron(')) {
      const inner = value.slice(5, -1).trim();
      const parts = inner.split(/\s+/);
      if (parts.length === 6) {
        const [minute, hour, dom, , dow] = parts;
        const h = parseInt(hour, 10);
        const m = parseInt(minute, 10);
        if (!isNaN(h) && !isNaN(m)) {
          setTime(`${pad(h)}:${pad(m)}`);
        }
        if (dow !== '?' && dow !== '*') {
          setFrequency('weekly');
          setSelectedDays(dow.split(','));
        } else if (dom !== '*' && dom !== '?') {
          setFrequency('monthly');
          setDayOfMonth(parseInt(dom, 10) || 1);
        } else {
          setFrequency('daily');
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build expression from builder state
  function buildExpression(): string {
    switch (frequency) {
      case 'minutes':
        return `rate(${minuteInterval} ${minuteInterval === 1 ? 'minute' : 'minutes'})`;
      case 'hourly':
        return `rate(1 hour)`;
      case 'daily': {
        const { hour, minute } = parseTime(time);
        return `cron(${minute} ${hour} * * ? *)`;
      }
      case 'weekly': {
        const { hour, minute } = parseTime(time);
        const days = selectedDays.length > 0 ? selectedDays.join(',') : 'MON';
        return `cron(${minute} ${hour} ? * ${days} *)`;
      }
      case 'monthly': {
        const { hour, minute } = parseTime(time);
        return `cron(${minute} ${hour} ${dayOfMonth} * ? *)`;
      }
    }
  }

  // When builder state changes, update expression
  useEffect(() => {
    if (advanced) return;
    const expr = buildExpression();
    setRawExpr(expr);
    onChange(expr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frequency, minuteInterval, time, selectedDays, dayOfMonth, advanced]);

  const nextExecutions = useMemo(() => computeNextExecutions(rawExpr, 3), [rawExpr]);

  function toggleDay(day: string) {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    );
  }

  const inputCls =
    'w-full rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-xs text-zinc-800 focus:outline-none focus:ring-2 focus:ring-emerald-400';
  const selectCls =
    'w-full rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-xs text-zinc-800 focus:outline-none focus:ring-2 focus:ring-emerald-400';

  return (
    <div className="space-y-3">
      {/* ── Presets ── */}
      <div>
        <label className="block text-xs font-medium text-zinc-600 mb-1.5">Quick presets</label>
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button
              key={p.expression}
              onClick={() => {
                setRawExpr(p.expression);
                onChange(p.expression);
                // Also update builder state to match
                if (p.expression.startsWith('rate(')) {
                  const inner = p.expression.slice(5, -1).trim();
                  const parts = inner.split(/\s+/);
                  const amount = parseInt(parts[0], 10);
                  const unit = parts[1] || '';
                  if (unit.startsWith('minute')) {
                    setFrequency('minutes');
                    setMinuteInterval(amount);
                  } else if (unit.startsWith('hour')) {
                    setFrequency('hourly');
                  }
                } else if (p.expression.startsWith('cron(')) {
                  const inner = p.expression.slice(5, -1).trim();
                  const parts = inner.split(/\s+/);
                  const [minute, hour, dom, , dow] = parts;
                  const h = parseInt(hour, 10);
                  const m = parseInt(minute, 10);
                  if (!isNaN(h) && !isNaN(m)) setTime(`${pad(h)}:${pad(m)}`);
                  if (dow !== '?' && dow !== '*') {
                    setFrequency('weekly');
                    setSelectedDays(dow.split(','));
                  } else if (dom !== '*' && dom !== '?') {
                    setFrequency('monthly');
                    setDayOfMonth(parseInt(dom, 10) || 1);
                  } else {
                    setFrequency('daily');
                  }
                }
                setAdvanced(false);
              }}
              className={`px-2 py-1 rounded-full text-xs font-medium border transition-colors ${
                rawExpr === p.expression
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                  : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Custom builder ── */}
      {!advanced && (
        <div className="space-y-2">
          <label className="block text-xs font-medium text-zinc-600">Frequency</label>
          <select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as Frequency)}
            className={selectCls}
          >
            <option value="minutes">Every X minutes</option>
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>

          {frequency === 'minutes' && (
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">
                Every (minutes)
              </label>
              <input
                type="number"
                min={1}
                max={1440}
                value={minuteInterval}
                onChange={(e) => setMinuteInterval(Math.max(1, parseInt(e.target.value, 10) || 1))}
                className={inputCls}
              />
            </div>
          )}

          {(frequency === 'daily' || frequency === 'weekly' || frequency === 'monthly') && (
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">Time (UTC)</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className={inputCls}
              />
            </div>
          )}

          {frequency === 'weekly' && (
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">Days of week</label>
              <div className="flex flex-wrap gap-1">
                {DAYS_OF_WEEK.map((d) => (
                  <button
                    key={d.value}
                    onClick={() => toggleDay(d.value)}
                    className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${
                      selectedDays.includes(d.value)
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                        : 'bg-white border-zinc-200 text-zinc-500 hover:border-zinc-300'
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {frequency === 'monthly' && (
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">Day of month</label>
              <input
                type="number"
                min={1}
                max={31}
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(Math.max(1, Math.min(31, parseInt(e.target.value, 10) || 1)))}
                className={inputCls}
              />
            </div>
          )}
        </div>
      )}

      {/* ── Raw expression ── */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-medium text-zinc-600">Expression</label>
          <button
            onClick={() => setAdvanced(!advanced)}
            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
          >
            {advanced ? 'Visual builder' : 'Advanced'}
          </button>
        </div>
        {advanced ? (
          <input
            type="text"
            value={rawExpr}
            onChange={(e) => {
              setRawExpr(e.target.value);
              onChange(e.target.value);
            }}
            placeholder="cron(0 9 ? * MON *) or rate(1 day)"
            className={inputCls}
          />
        ) : (
          <div className="px-2.5 py-1.5 rounded-md bg-zinc-50 border border-zinc-200 text-xs text-zinc-700 font-mono">
            {rawExpr || <span className="italic text-zinc-400">Not set</span>}
          </div>
        )}
        {(() => {
          const expr = rawExpr || '';
          const isValid = expr === '' || expr.startsWith('cron(') || expr.startsWith('rate(');
          if (!isValid) {
            return (
              <p className="text-xs text-red-400 mt-1">
                Use AWS EventBridge syntax: <code>cron(0 9 ? * MON *)</code> or{' '}
                <code>rate(1 day)</code>
              </p>
            );
          }
          return null;
        })()}
      </div>

      {/* ── Next executions ── */}
      {rawExpr && nextExecutions.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-zinc-600 mb-1">
            Next executions
          </label>
          <div className="space-y-0.5">
            {nextExecutions.map((t, i) => (
              <p key={i} className="text-xs text-zinc-500 font-mono">
                {t}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
